import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {Resource} from '../../../resource/resource';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';
import {Observable, of as observableOf} from 'rxjs';
import {map, mergeMap, shareReplay} from 'rxjs/operators';
import {ResourceService} from '../../../resource/resource.service';
import {CacheService} from '../../../cache/cache.service';
import {DisplayAttribute} from '../../../resource/displayattribute';

@Component({
  selector: 'timeline-workflow-table',
  templateUrl: './workflow-table.component.html',
  styles: [
    'td, th { padding: 0 0.5rem; border-bottom: 1px solid var(--mat-sys-outline, rgba(0, 0, 0, 0.12)); }',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkflowTableComponent {
  constructor(
    private changeDetector: ChangeDetectorRef,
    private resourceService: ResourceService,
    private cacheService: CacheService,
    private envConfig: EnvironmentConfigService,
  ) {
    const daObs: Observable<DisplayAttribute>[] = [];
    daObs.push(
      this.cacheService.getDisplayAttribute(
        this.envConfig.getAttributeName('WorkflowDefinition'),
        this.envConfig.getObjectTypeName('WorkflowInstance'),
      ),
    );
    daObs.push(
      this.cacheService.getDisplayAttribute(
        this.envConfig.getAttributeName('RequestPhase'),
        this.envConfig.getObjectTypeName('WorkflowDefinition'),
      ),
    );
    daObs.push(
      this.cacheService.getDisplayAttribute(
        this.envConfig.getAttributeName('WorkflowStatus'),
        this.envConfig.getObjectTypeName('WorkflowInstance'),
      ),
    );
    daObs.push(
      this.cacheService.getDisplayAttribute(
        this.envConfig.getAttributeName('WorkflowStatusDetail'),
        this.envConfig.getObjectTypeName('WorkflowInstance'),
      ),
    );

    this.columnNames = daObs.map(obs =>
      obs.pipe(map(da => ResourceService.convertCamelCaseToNormal(da.DisplayName))),
    );
  }

  rows: WorkflowTableRowModel[] = [];

  @Output() openDetail: EventEmitter<any> = new EventEmitter();

  // Getter for template access to environment attributes
  get myEnv() {
    return {
      Attributes: {
        ObjectID: this.envConfig.getAttributeName('ObjectID'),
        DisplayName: this.envConfig.getAttributeName('DisplayName'),
        WorkflowDefinition: this.envConfig.getAttributeName('WorkflowDefinition'),
        WorkflowStatus: this.envConfig.getAttributeName('WorkflowStatus'),
        WorkflowStatusDetail: this.envConfig.getAttributeName('WorkflowStatusDetail'),
      },
    };
  }

  @Input() set request(value: Resource) {
    if (value === null) {
      this.rows = [];
    } else {
      this.rows = [
        ...this.buildRowModels(
          value[this.envConfig.getAttributeName('ActionWorkflowInstance')],
          'Action',
        ),
        ...this.buildRowModels(
          value[this.envConfig.getAttributeName('AuthenticationWorkflowInstance')],
          'Authentication',
        ),
        ...this.buildRowModels(
          value[this.envConfig.getAttributeName('AuthorizationWorkflowInstance')],
          'Authorization',
        ),
      ];
    }
    this.changeDetector.markForCheck();
  }

  columnNames: Observable<string>[];

  private buildRowModels(
    workflows: any[],
    type: 'Action' | 'Authentication' | 'Authorization',
  ): WorkflowTableRowModel[] {
    if (!Array.isArray(workflows) || workflows.length === 0) {
      return [];
    }
    const filter =
      '/' +
      this.envConfig.getObjectTypeName('WorkflowInstance') +
      '[' +
      workflows
        .map(guid => this.envConfig.getAttributeName('ObjectID') + '="' + guid + '"')
        .join(' or ') +
      ']';
    const fullObs = this.resourceService
      .searchResources(
        filter,
        [
          this.envConfig.getAttributeName('ObjectID'),
          this.envConfig.getAttributeName('WorkflowDefinition'),
          this.envConfig.getAttributeName('WorkflowStatus'),
          this.envConfig.getAttributeName('WorkflowStatusDetail'),
        ]
          .filter(x => x !== null)
          .join(','),
        workflows.length,
      )
      .pipe(shareReplay(1));

    return workflows.map(wfGuid => {
      const rm = new WorkflowTableRowModel();
      rm.type = type;
      rm.guid = wfGuid;
      rm.observable = fullObs.pipe(
        map(fr => {
          if (fr.TotalCount === 0 || fr.Results.length === 0) return null;
          const wfList = fr.Results.filter(
            r => r[this.envConfig.getAttributeName('ObjectID')] === wfGuid,
          );
          return wfList.length > 0 ? wfList[0] : null;
        }),
      );
      rm.definitionGuid = rm.observable.pipe(
        map(wfi => wfi?.[this.envConfig.getAttributeName('WorkflowDefinition')]),
      );
      rm.definitionResource = rm.definitionGuid.pipe(
        mergeMap(wfdGuid => this.resourceService.getBasicResource(wfdGuid)),
      );
      rm.status = rm.observable.pipe(
        map(wfi => wfi?.[this.envConfig.getAttributeName('WorkflowStatus')]),
      );
      rm.statusDetail = rm.observable.pipe(
        map(wfi => wfi?.[this.envConfig.getAttributeName('WorkflowStatusDetail')]),
      );
      return rm;
    });
  }
}

class WorkflowTableRowModel {
  type: 'Action' | 'Authentication' | 'Authorization';
  guid: string;
  observable: Observable<any>;
  definitionGuid: Observable<string>;
  definitionResource: Observable<any>;
  status: Observable<string>;
  statusDetail: Observable<string[]>;
}
